import { HttpClient } from '../../../services/api/client'
import { TokenManager } from '../../../services/auth/tokenManager'
import type {
  ApiResponse,
  CreateSurveyRequest,
  SurveyRecord,
  UpdateSurveyRequest,
} from '@bandos/shared'

/**
 * CRUD client for surveys scoped to a mindmap. Replaces the old mindmap-save
 * round-trip that used to mutate `node.data.surveyQuestions` in the JSONB.
 */
export class MindmapSurveyService {
  static async list(mindmapId: string): Promise<ApiResponse<{ surveys: SurveyRecord[] }>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.get<{ surveys: SurveyRecord[] }>(`/mindmaps/${mindmapId}/surveys`, headers)
  }

  static async get(
    mindmapId: string,
    surveyId: string
  ): Promise<ApiResponse<{ survey: SurveyRecord }>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.get<{ survey: SurveyRecord }>(
      `/mindmaps/${mindmapId}/surveys/${surveyId}`,
      headers
    )
  }

  static async create(
    mindmapId: string,
    body: CreateSurveyRequest
  ): Promise<ApiResponse<{ survey: SurveyRecord }>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.post<{ survey: SurveyRecord }>(
      `/mindmaps/${mindmapId}/surveys`,
      body,
      headers
    )
  }

  static async update(
    mindmapId: string,
    surveyId: string,
    body: UpdateSurveyRequest
  ): Promise<ApiResponse<{ survey: SurveyRecord }>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.patch<{ survey: SurveyRecord }>(
      `/mindmaps/${mindmapId}/surveys/${surveyId}`,
      body,
      headers
    )
  }

  static async delete(
    mindmapId: string,
    surveyId: string
  ): Promise<ApiResponse<{ success: true }>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.delete<{ success: true }>(
      `/mindmaps/${mindmapId}/surveys/${surveyId}`,
      headers
    )
  }

  /**
   * Lock the survey to new responses and trigger fire-and-forget LLM synthesis.
   * The synthesis result lands asynchronously via the `survey:{id}` realtime channel.
   */
  static async complete(
    mindmapId: string,
    surveyId: string
  ): Promise<ApiResponse<{ survey: SurveyRecord }>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.post<{ survey: SurveyRecord }>(
      `/mindmaps/${mindmapId}/surveys/${surveyId}/complete`,
      {},
      headers
    )
  }

  /**
   * Reopen a previously closed survey. Existing synthesis is preserved but
   * may become stale once new responses arrive.
   */
  static async reopen(
    mindmapId: string,
    surveyId: string
  ): Promise<ApiResponse<{ survey: SurveyRecord }>> {
    const headers = await TokenManager.getAuthHeaders()
    return HttpClient.post<{ survey: SurveyRecord }>(
      `/mindmaps/${mindmapId}/surveys/${surveyId}/reopen`,
      {},
      headers
    )
  }
}
